import {
  Alert,
  Box,
  Button,
  ContentLayout,
  FormField,
  Header,
  Input,
  Modal,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import MDEditor from '@uiw/react-md-editor';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { WikiPage } from '../../models/Wiki';
import {
  canEdit,
  canPublish,
  deleteWikiPage,
  fixInternalLinksAfterRename,
  getWikiPage,
  listSubpages,
  previewDeleteCascade,
  renameWikiPageCascade,
} from './Wiki.logic';
import { useSession } from './Wiki.shared';

const WikiView = () => {
  const { slug = '' } = useParams();
  const { tenantId, role, userId } = useSession();
  const navigate = useNavigate();

  const [page, setPage] = useState<WikiPage | undefined>();
  const [subs, setSubs] = useState<WikiPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // dialogs
  const [showDelete, setShowDelete] = useState(false);
  const [deleteCount, setDeleteCount] = useState<number | null>(null);

  const [showRename, setShowRename] = useState(false);
  const [newSlug, setNewSlug] = useState(slug);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const [p, s] = await Promise.all([getWikiPage(tenantId, slug), listSubpages(tenantId, slug)]);
        if (!mounted) return;
        setPage(p);
        setSubs(s);
      } catch (e: any) {
        if (!mounted) return;
        setErr(e.message || String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [tenantId, slug]);

  const breadcrumbs = useMemo(() => {
    const parts = slug.split('-').filter(Boolean);
    const acc: Array<{ text: string; slug: string }> = [];
    for (let i = 0; i < parts.length; i++) {
      const s = parts.slice(0, i + 1).join('-');
      acc.push({ text: s, slug: s });
    }
    return acc;
  }, [slug]);

  useEffect(() => {
    setNewSlug(slug);
  }, [slug]);

  if (loading) {
    return (
      <ContentLayout header={<Header>Loading…</Header>}>
        <Spinner /> Loading…
      </ContentLayout>
    );
  }

  if (!page) {
    return (
      <ContentLayout header={<Header>Not found</Header>}>
        <Box>Page not found.</Box>
      </ContentLayout>
    );
  }

  const editable = canEdit(page, role, userId);
  const publishable = canPublish(role);

  async function onAskDelete() {
    try {
      const { totalPages } = await previewDeleteCascade(tenantId, slug);
      setDeleteCount(totalPages);
      setShowDelete(true);
    } catch (e: any) {
      setErr(e.message || String(e));
    }
  }

  async function onConfirmDelete() {
    try {
      await deleteWikiPage(tenantId, slug);
      navigate('/wiki');
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setShowDelete(false);
    }
  }

  async function onConfirmRename() {
    try {
      if (!newSlug || newSlug === slug) {
        setShowRename(false);
        return;
      }
      await renameWikiPageCascade(tenantId, slug, newSlug, userId, { moveRevisions: true });
      await fixInternalLinksAfterRename(tenantId, slug, newSlug, userId);
      navigate(`/wiki/${newSlug}`);
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setShowRename(false);
    }
  }

  return (
    <ContentLayout
      disableOverlap
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowRename(true)} disabled={!editable}>
                Rename
              </Button>
              <Button onClick={() => navigate(`/wiki/${slug}/edit`)} disabled={!editable}>
                Edit
              </Button>
              <Button onClick={onAskDelete} disabled={!editable}>
                Delete
              </Button>
              <Button variant="primary" onClick={() => navigate(`/wiki/new?parent=${slug}`)}>
                New subpage
              </Button>
            </SpaceBetween>
          }
        >
          {page.title}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {err && (
          <Alert type="error" header="Error">
            {err}
          </Alert>
        )}

        {/* Breadcrumbs */}
        <Box variant="p" color="text-body-secondary">
          {breadcrumbs.map((b, i) => (
            <React.Fragment key={b.slug}>
              <Link to={`/wiki/${b.slug}`}>{b.text}</Link>
              {i < breadcrumbs.length - 1 && ' / '}
            </React.Fragment>
          ))}
        </Box>

        {/* Meta */}
        <Box>
          <Box variant="code" color="text-status-inactive">
            {page.status}
          </Box>{' '}
          <Box variant="code">{page.visibility}</Box>{' '}
          {page.pinned && (
            <Box variant="code" color="text-status-success">
              pinned
            </Box>
          )}
          {(page.tags?.length || page.categories?.length) && (
            <Box margin={{ top: 'xs' }} color="text-body-secondary">
              {page.tags?.length ? <>tags: {page.tags.join(', ')} </> : null}
              {page.categories?.length ? <>• categories: {page.categories.join(', ')}</> : null}
            </Box>
          )}
        </Box>

        {/* Content */}
        <div data-color-mode="light">
          <MDEditor.Markdown source={page.contentMd || ''} />
        </div>

        {/* Subpages */}
        <Box>
          <strong>Subpages</strong>
          <Box margin={{ top: 'xs' }}>
            {subs.length === 0 ? (
              <Box color="text-body-secondary">No subpages yet.</Box>
            ) : (
              <ul>
                {subs.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/wiki/${s.slug}`}>{s.title}</Link>
                    {s.status !== 'published' && (
                      <Box margin={{ left: 'xs' }} variant="code" color="text-status-inactive">
                        {s.status}
                      </Box>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Box>
        </Box>
      </SpaceBetween>

      {/* Delete confirm */}
      <Modal
        visible={showDelete}
        onDismiss={() => setShowDelete(false)}
        header={`Delete “${page.title}”?`}
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="normal" onClick={() => setShowDelete(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onConfirmDelete}>
              Delete
            </Button>
          </SpaceBetween>
        }
      >
        <Box>
          This will delete <strong>{deleteCount ?? 0}</strong> page(s): the page and all its subpages. This cannot be
          undone.
        </Box>
      </Modal>

      {/* Rename dialog */}
      <Modal
        visible={showRename}
        onDismiss={() => setShowRename(false)}
        header={`Rename “${page.title}”`}
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setShowRename(false)}>Cancel</Button>
            <Button variant="primary" onClick={onConfirmRename} disabled={!newSlug}>
              Rename
            </Button>
          </SpaceBetween>
        }
      >
        <FormField label="New slug">
          <Input value={newSlug} onChange={(e) => setNewSlug(e.detail.value)} />
        </FormField>
        <Box variant="p" color="text-body-secondary">
          All subpages will move to the new slug prefix.
        </Box>
      </Modal>
    </ContentLayout>
  );
};

export default WikiView;
